/**
 * The finding page's decision control, as plain rules: which action leads the
 * top bar, which ways of resolving a finding are on offer, and what the
 * confirm button says. The component (`FindingDecisionControl`) and the form
 * (`ResolveFindingForm`) only render what these return.
 *
 * @module components/regulation/finding/resolveControl
 */
import { DISMISS_CATEGORY_LABELS } from "@repo/shared/findingPlanning";

import { MIN_COVERED_NOTE_LENGTH } from "../policy/resolutionCandidates";
import type { FindingDecision, FindingItemStatus } from "./findingItem";

/**
 * The top bar's lead:
 * - `apply`: Apply fix, with the other ways to resolve in its menu.
 * - `resolve`: Resolve…, opening the resolve form.
 * - `reopen`: the decision's status pill and Reopen.
 * - `status`: the pill alone, for someone who cannot decide.
 * - `none`: nothing, for someone who cannot decide an open finding.
 */
export type HeaderDecision = "apply" | "resolve" | "reopen" | "status" | "none";

export function headerDecision(input: {
  readonly status: FindingItemStatus;
  readonly canManage: boolean;
  /** A current proposed fix exists and may be applied. */
  readonly canApply: boolean;
}): HeaderDecision {
  if (input.status !== "open") return input.canManage ? "reopen" : "status";
  if (!input.canManage) return "none";
  return input.canApply ? "apply" : "resolve";
}

/** What is being resolved: a regulatory finding, a control amendment, or a missing document. */
export type ResolveTarget = "finding" | "amendment" | "missing";

/**
 * One way to resolve:
 * - `newer`: a version of the document already carries the change.
 * - `covered`: the current version already answers it; a note says how.
 * - `upload`: upload the revised PDF as the next version.
 * - `document`: a document uploaded since the gap was raised fills it.
 * - `upload-document`: upload the missing document.
 */
export type ResolveChoice = "newer" | "covered" | "upload" | "document" | "upload-document";

/**
 * The choices for a target, in the order they are offered. A finding with an
 * assessment stamp may also be resolved as covered by the current version;
 * without a stamp every version is already a candidate, so that path is not needed.
 */
export function resolveChoices(target: ResolveTarget, options: { readonly stamped: boolean }): ReadonlyArray<ResolveChoice> {
  if (target === "missing") return ["document", "upload-document"];
  if (target === "amendment") return ["newer", "upload"];
  return options.stamped ? ["newer", "covered", "upload"] : ["newer", "upload"];
}

/**
 * The choice the form opens on: an existing version or document when there is
 * one, else "already covered" for a stamped finding, else an upload.
 */
export function defaultResolveChoice(
  choices: ReadonlyArray<ResolveChoice>,
  available: { readonly versions: number; readonly documents: number },
): ResolveChoice {
  if (choices.includes("newer") && available.versions > 0) return "newer";
  if (choices.includes("document") && available.documents > 0) return "document";
  if (choices.includes("covered")) return "covered";
  return choices.includes("upload-document") ? "upload-document" : "upload";
}

/** "File v5", or "Text v2" for a text capture (they number independently). */
export const versionName = (candidate: { readonly version: number; readonly label: "file" | "text" }): string =>
  `${candidate.label === "text" ? "Text" : "File"} v${candidate.version}`;

export interface ConfirmContext {
  /** The picked version, for `newer`. */
  readonly picked: string | null;
  /** The document's current version, for `covered`. */
  readonly currentVersion: number | null;
  /** The picked document's name, for `document`. */
  readonly documentName: string | null;
  /** A version already uploaded by an earlier attempt that failed to record the decision. */
  readonly uploadedVersion?: number | undefined;
}

/** The confirm button names exactly what will be recorded: "Resolve with File v5". */
export function resolveConfirmLabel(choice: ResolveChoice, context: ConfirmContext): string {
  switch (choice) {
    case "newer":
      return context.picked ? `Resolve with ${context.picked}` : "Resolve";
    case "covered":
      return context.currentVersion === null ? "Resolve" : `Resolve with File v${context.currentVersion}`;
    case "upload":
      if (context.uploadedVersion !== undefined) return `Resolve with File v${context.uploadedVersion}`;
      return context.currentVersion === null ? "Upload and resolve" : `Upload File v${context.currentVersion + 1} and resolve`;
    case "document":
      return context.documentName ? `Resolve with ${context.documentName}` : "Resolve";
    case "upload-document":
      return "Upload and resolve";
  }
}

/**
 * Why the confirm button is not ready yet, or null when it is. Said under the
 * choice, so a disabled button never stands without a reason.
 */
export function resolveBlocker(
  choice: ResolveChoice,
  input: {
    readonly versions: number;
    readonly documents: number;
    readonly hasFile: boolean;
    readonly currentVersion: number | null;
    /** The "already covered" note, as typed. */
    readonly coveredNote: string;
  },
): string | null {
  switch (choice) {
    case "newer":
      return input.versions === 0 ? "No newer version yet." : null;
    case "covered": {
      if (input.currentVersion === null) return "The current version is still loading.";
      const missing = MIN_COVERED_NOTE_LENGTH - input.coveredNote.trim().length;
      if (missing <= 0) return null;
      return input.coveredNote.trim().length === 0 ? "Say where it is covered." : `${missing} more ${missing === 1 ? "character" : "characters"}.`;
    }
    case "upload":
    case "upload-document":
      return input.hasFile ? null : "Choose the PDF.";
    case "document":
      return input.documents === 0 ? "Nothing has been uploaded since this gap was raised." : null;
  }
}

/** The decided finding's pill in the top bar: "Resolved · File v5", "Dismissed · Already covered". */
export function decisionPillLabel(status: Exclude<FindingItemStatus, "open">, decision: FindingDecision | null): string {
  const label = status === "resolved" ? "Resolved" : "Dismissed";
  const detail =
    decision === null
      ? null
      : status === "resolved"
        ? decision.evidence
        : decision.category
          ? DISMISS_CATEGORY_LABELS[decision.category]
          : null;
  return detail ? `${label} · ${detail}` : label;
}

/** The menu wording for each way to resolve, when Apply fix leads. */
export function resolveChoiceLabel(choice: ResolveChoice, currentVersion: number | null): string {
  switch (choice) {
    case "newer":
      return "Fixed in a newer version…";
    case "covered":
      return currentVersion === null ? "Already covered by the current version…" : `Already covered by File v${currentVersion}…`;
    case "upload":
      return "Upload a new version…";
    case "document":
      return "Already in the library…";
    case "upload-document":
      return "Upload the document…";
  }
}

/** What a finding-page item resolves as; null for an item with nothing to resolve against. */
export function resolveTargetOf(item: {
  readonly finding: { readonly id: string } | null;
  readonly suggestion: { readonly kind: string } | null;
  readonly policyId: string | null;
}): ResolveTarget | null {
  if (item.finding) return item.policyId ? "finding" : null;
  if (item.suggestion?.kind === "missing_document") return "missing";
  return item.policyId ? "amendment" : null;
}
